import { DAY_MS, matchesInsightFilters, type InsightRow } from './insight-rows'
import type { InsightSpec } from './insight-spec'

// Which rows a spec works over — its filters and time window — and the period before it.

/** The span a spec looks at, in ms, both ends inclusive — from `dateFrom`/`dateTo` and `lastDays`
 *  (counted back from the newest row inside the range). Null ends are open. */
function windowOf(matched: InsightRow[], spec: InsightSpec): { from: number; to: number } {
  let from = spec.dateFrom ? Date.parse(`${spec.dateFrom}T00:00:00Z`) : -Infinity
  let to = spec.dateTo ? Date.parse(`${spec.dateTo}T00:00:00Z`) + DAY_MS - 1 : Infinity
  if (spec.lastDays !== null) {
    const newest = matched.reduce((max, row) => {
      const at = Date.parse(row.publishedAt)
      return at <= to ? Math.max(max, at) : max
    }, -Infinity)
    // "The last N days" ends at the newest article — so the period before it has a length.
    if (Number.isFinite(newest)) {
      to = Math.min(to, newest)
      from = Math.max(from, newest - spec.lastDays * DAY_MS + 1)
    }
  }
  return { from, to }
}

const within = (row: InsightRow, window: { from: number; to: number }) => {
  const at = Date.parse(row.publishedAt)
  return at >= window.from && at <= window.to
}

/** The rows a spec works over: the scope, its `where`, and its time window. */
export function scopedRows(scope: InsightRow[], spec: InsightSpec): InsightRow[] {
  const matched = scope.filter((row) => matchesInsightFilters(row, spec.where))
  if (spec.lastDays === null && !spec.dateFrom && !spec.dateTo) return matched
  const window = windowOf(matched, spec)
  return matched.filter((row) => within(row, window))
}

/**
 * The period of equal length just before the spec's window, for `compare: 'previous'` — with a note
 * when there is no window to mirror, or the loaded coverage does not reach back over all of it.
 */
export function previousPeriod(
  scope: InsightRow[],
  spec: InsightSpec,
): { rows: InsightRow[]; note: string | null } | null {
  if (spec.compare !== 'previous') return null
  const matched = scope.filter((row) => matchesInsightFilters(row, spec.where))
  if (spec.lastDays === null && !(spec.dateFrom && spec.dateTo)) {
    return {
      rows: [],
      note: 'Comparing needs a period — say how many days, or give both dates.',
    }
  }
  const window = windowOf(matched, spec)
  if (!Number.isFinite(window.from) || !Number.isFinite(window.to)) {
    return { rows: [], note: 'There is no coverage in that period to compare.' }
  }
  const length = window.to - window.from + 1
  const previous = { from: window.from - length, to: window.from - 1 }
  const earliest = scope.reduce((min, row) => Math.min(min, Date.parse(row.publishedAt)), Infinity)
  return {
    rows: matched.filter((row) => within(row, previous)),
    note:
      earliest > previous.from
        ? 'The loaded coverage does not reach back over the whole previous period, so it may read low — widen the date range on the page.'
        : null,
  }
}

export const THIS_PERIOD = 'This period'
export const PREVIOUS_PERIOD = 'Previous period'
